import { ArrowRight, ArrowUpRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

import { compactHours, DIRECTIONS_HREF, OFFICE } from '@/lib/office'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The foot of the equipment listing: where to collect, and the three terms
 * worth knowing — as a bento, not a list of rules.
 *
 * Owner's brief for the redesign: advantages, not threats („straszymy karą,
 * mają być zalety"), professional rather than playful, and quieter than the
 * cards above it. So the 200% late fee is not on this page any more — it is
 * still in the regulamin and on every item page (`RentalTerms`), next to the
 * control that commits to a date. What stays here is what makes renting
 * easy: no deposit, the cheapest day, and a day that starts at pickup.
 *
 * Plain white type, no gradient on the words, hairline tiles with almost no
 * fill: the photograph is the only loud thing in the band.
 */
export function RentalListingTerms({
  locale,
  fromDailyNet,
  className,
}: {
  locale: string
  /** The cheapest daily rate in the catalogue, net — `null` hides the tile's figure. */
  fromDailyNet: number | null
  className?: string
}) {
  const hours = compactHours()
    .filter((run) => run.open)
    .map((run) => `${run.label} ${run.open}`)
    .join(' · ')

  return (
    <section
      aria-labelledby="warunki-wynajmu"
      className={cn(
        'grid grid-cols-1 gap-3 md:grid-cols-4 md:grid-rows-[repeat(2,minmax(8.125rem,auto))]',
        className,
      )}
    >
      <h2 id="warunki-wynajmu" className="sr-only">
        Warunki wynajmu
      </h2>

      <div className="relative min-h-72 overflow-hidden rounded-[1.375rem] border border-white/10 md:col-span-2 md:row-span-2">
        <Image
          src="/studio/solo-1.webp"
          alt="Studio FLOU w Radomierzycach — tu odbierasz sprzęt."
          fill
          sizes="(min-width: 768px) 50vw, 100vw"
          className="object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-[oklch(0.08_0.012_300/0.92)] via-[oklch(0.08_0.012_300/0.2)] via-60% to-transparent"
        />
        <div className="absolute inset-x-7 bottom-7 flex flex-wrap items-end justify-between gap-3.5">
          <div>
            <p className={cn(META, 'text-white/75')}>Odbiór osobisty lub paczkomat InPost</p>
            <p className="mt-1 font-masthead text-[1.3rem] font-semibold tracking-tight text-white">
              {OFFICE.street}, {OFFICE.town}
            </p>
            <p className={cn(META, 'mt-1 text-white/70')}>{hours}</p>
          </div>
          <a
            href={DIRECTIONS_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-[1.125rem] text-[0.85rem] font-semibold text-neutral-900 transition-colors hover:bg-white/90"
          >
            Wyznacz trasę
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="flex flex-col justify-between gap-3 rounded-[1.375rem] border border-white/[0.08] bg-white/[0.025] p-[1.375rem] md:col-span-2">
        <p className={cn(META, 'text-white/55')}>Kaucja</p>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="font-masthead text-[1.6rem] leading-tight font-semibold tracking-tight text-white/90">
            Bez kaucji.
          </p>
          <Link
            href={`/${locale}/regulamin`}
            className="inline-flex min-h-11 items-center gap-1.5 text-[0.85rem] font-medium text-white/60 transition-colors hover:text-white"
          >
            Regulamin
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>

      {fromDailyNet === null ? null : (
        <div className="flex flex-col justify-between gap-3 rounded-[1.375rem] border border-white/[0.08] bg-white/[0.025] p-[1.375rem]">
          <p className={cn(META, 'text-white/55')}>Cena za dobę</p>
          <p className="font-masthead text-[1.3rem] leading-tight font-semibold tracking-tight text-white/90">
            od {fromDailyNet}&nbsp;zł{' '}
            <span className="font-sans text-[0.8rem] font-medium tracking-normal text-white/60">
              netto
            </span>
          </p>
        </div>
      )}

      <div className="flex flex-col justify-between gap-3 rounded-[1.375rem] border border-white/[0.08] bg-white/[0.025] p-[1.375rem]">
        <p className={cn(META, 'text-white/55')}>Doba liczona</p>
        <p className="font-masthead text-[1.3rem] leading-tight font-semibold tracking-tight text-white/90">
          od odbioru
        </p>
      </div>
    </section>
  )
}
